import { motion } from 'framer-motion'
import { AlertTriangle, Clock, FolderKanban, Inbox } from 'lucide-react'
import type { FinanceStatTarget } from '@/hooks/useFinanceiroPage'
import type { FinanceSummary } from '@/services/financeService'
import { cn } from '@/utils/cn'

type StatItem = {
  id: FinanceStatTarget
  label: string
  value: number
  icon: typeof Inbox
  accent: string
  iconBg: string
  valueClass?: string
  delay?: number
}

type StatCardProps = StatItem & {
  onClick?: (target: FinanceStatTarget) => void
}

function FinanceStatCard({
  id,
  label,
  value,
  icon: Icon,
  accent,
  iconBg,
  valueClass,
  delay = 0,
  onClick,
}: StatCardProps) {
  const content = (
    <>
      <div
        className='absolute left-0 top-0 h-[2px] w-full bg-gradient-to-r from-transparent to-transparent'
        style={{ backgroundImage: `linear-gradient(to right, transparent, ${accent}66, transparent)` }}
      />
      <div className='mb-2 flex items-center justify-between'>
        <div
          className='flex h-10 w-10 items-center justify-center rounded-xl'
          style={{ backgroundColor: iconBg, color: accent }}
        >
          <Icon size={20} />
        </div>
      </div>
      <p
        className='mb-1 font-medium uppercase tracking-wider text-[var(--font-color-label)]'
        style={{ fontSize: 'var(--font-size-label)' }}
      >
        {label}
      </p>
      <p className={cn('text-2xl font-black text-[var(--font-color-body)]', valueClass)}>{value}</p>
    </>
  )

  const className = cn(
    'glass-card relative overflow-hidden p-4 text-left transition-all',
    onClick && 'cursor-pointer hover:border-accent/30 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
  )

  if (!onClick) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay }}
        className={className}
      >
        {content}
      </motion.div>
    )
  }

  return (
    <motion.button
      type='button'
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={className}
      onClick={() => onClick(id)}
      aria-label={`${label}: ${value}. Ver detalhes`}
    >
      {content}
    </motion.button>
  )
}

type Props = {
  summary: FinanceSummary
  onStatClick?: (target: FinanceStatTarget) => void
}

export function FinanceStatsCards({ summary, onStatClick }: Props) {
  const cards: StatItem[] = [
    {
      id: 'open_requests',
      label: 'Chamados abertos',
      value: summary.openRequests,
      icon: Inbox,
      accent: '#ef4444',
      iconBg: 'rgba(239, 68, 68, 0.12)',
    },
    {
      id: 'sla_due_soon',
      label: 'SLA vencendo (24h)',
      value: summary.slaDueSoon,
      icon: Clock,
      accent: '#f59e0b',
      iconBg: 'rgba(245, 158, 11, 0.12)',
      valueClass: summary.slaDueSoon > 0 ? 'text-amber-500' : undefined,
      delay: 0.05,
    },
    {
      id: 'sla_overdue',
      label: 'SLA vencido',
      value: summary.overdueSla,
      icon: AlertTriangle,
      accent: '#ef4444',
      iconBg: 'rgba(239, 68, 68, 0.12)',
      valueClass: summary.overdueSla > 0 ? 'text-red-500' : undefined,
      delay: 0.1,
    },
    {
      id: 'projects_no_contract',
      label: 'Projetos sem contrato',
      value: summary.projectsWithoutContract,
      icon: FolderKanban,
      accent: '#6366f1',
      iconBg: 'rgba(99, 102, 241, 0.12)',
      valueClass: summary.projectsWithoutContract > 0 ? 'text-indigo-400' : undefined,
      delay: 0.15,
    },
  ]

  return (
    <div className='mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
      {cards.map((card) => (
        <FinanceStatCard key={card.id} {...card} onClick={onStatClick} />
      ))}
    </div>
  )
}
